'use client';

import { Dispatch, SetStateAction, useEffect } from 'react';
import { ApiError, ApiService } from '@services/api/ApiService';
import {
	AccountType,
	Address,
	AddressFormValues,
	AddressWithoutId,
	ClientContact,
	ClientContactFormValues,
	ClientDetailsFormValues,
	ClientType,
	DqpAccountType,
	DqpDetailsFormValues,
	UserContactFormValues,
	UserIdentityType,
} from '@types';
import { convertDatetoStrYYYYMMDD, getCurrentDateTime, mapAddress } from '@utils';
import clientContactEmptyData from '@utils/sampleData/forms/clientContactEmpty.json';
import clientDetailsEmptyData from '@utils/sampleData/forms/clientDetailsEmpty.json';
import { random } from 'lodash';
import {
	EventType,
	FieldErrors,
	FieldNamesMarkedBoolean,
	FieldValues,
	UseFormReturn,
} from 'react-hook-form';
import * as Yup from 'yup';
import { AnyObject, AnyObjectSchema, SchemaDescription, ValidationError } from 'yup';

import { ActionableFormValues, ModuleASubformName } from './moduleAForm/ModuleAFormSchemas';

type DirtyFields = Partial<Readonly<FieldNamesMarkedBoolean<FieldValues>>>;
export type FormEditability<T> = Partial<Record<keyof T, boolean>>;
export type FormErrors<T> = Partial<Record<keyof T, string>>;
export type FormLabels<T> = Record<keyof T, string>;
export type FormAny = UseFormReturn<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
export type NestedArrayForm<T> = { list: T[] };
export type SelectOption = { value: string; label: string };

export const emailRegExp =
	/^([\w-\.\+]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,10}|[0-9]{1,3})(\]?)$/;
export const numberRegExp = /^[0-9]+$/;
export const sizeRegExp = /^[0-9]{1,4}$/;
export const aussiePhoneRegExp = /^0\d{9}$/;
export const aussieMobilePhoneRegExp = /^\+614\d{8}$/;
export const cmiNumberRegExp = /^\d{4,10}$/;
export const alternatePhoneRegExp = /^\+\d{8,18}$/;
export const standardTextOnlyRegExp = /^[\w|\s|-]*$/;
// latitude location between -90 and 90 with exactly 5 decimal places
export const latitudeRegExp = /^-?([1-8]?[1-9]|[1-9]0)\.{1}\d{5}$/;
// longitude location between -180 and 180 with exactly 5 decimal places
export const longitudeRegExp =
	/^-?([1]?[0-7]?[0-9]|[1]?[8][0]|[0-9]|[1-9][0-9]|[1][1][0-7])\.{1}\d{5}$/;
// Matches text containing only alphanumeric characters, spaces, full stops, commas,...
export const safeAnnotationTextRegExp = /^[a-zA-Z0-9\s.,()#\-'/@_]*$/;
export const displayNameRegExp = /^[a-zA-Z0-9 _'-]*$/;

export const latitudePlaceholder = 'DD.DDDDD';
export const longitudePlaceholder = 'DDD.DDDDD';
// export const aussiePhoneMessage = 'Invalid mobile phone number';
export const mobilePhoneMessage = 'Invalid mobile phone number';
export const aussiePhoneMessage =
	'Please enter a valid Australian mobile or landline telephone number';
export const cmiNumberValidationMessage =
	'The value must be a number with at least 4 digits and no more than 10 digits';
export const detailedTextOnlyMessage =
	'must only include alphanumeric characters, spaces, hyphens, underscores, parentheses, commas, full stops and hashes';
export const latitudeMessage = 'Please enter a latitude between -90 and 90 with 5 decimals';
export const longitudeMessage = 'Please enter a longitude between -180 and 180 with 5 decimals';
export const numberValidationMessage = 'The value must be a number';
export const sizeRangeValidationMessage =
	'The value must be an integer greater than 0 and less than 10,000.';
export const widthRangeValidationMessage =
	'The value must be greater than 0 and less than 10,000.00, with up to two decimal places.';
export const requiredMsg = (label: string): string => `${label} is required`;
export const minLengthErrorMsg = (length: number): string => `Must be minimum ${length} characters`;
export const maxLengthErrorMsg = (length: number): string => `Must be maximum ${length} characters`;
export const optionRequiredMessage = 'Select an option';
export const standardTextOnlyMessage =
	'must only include alphanumeric characters, spaces, hyphens and underscores';
export const safeAnnotationTextErrorMessage =
	'This field can only contain letters, numbers, spaces, and these characters: full stops, commas, brackets, hashes, hyphens, apostrophes, forward slashes, at symbols, and underscores';
export const displayNameErrorMessage =
	'This field can only contain letters, numbers, spaces, hyphens, apostrophes, and underscores';

// HELPER VALIDATION FUNCTIONS
export const emptyToUndefined = (value: Yup.AnyObject, originalValue: string) =>
	originalValue === '' ? undefined : value;
export const naNToUndefined = (value: Yup.AnyObject) => (Number.isNaN(value) ? undefined : value);

export const resetForm = <
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
) => {
	form.reset(undefined, { keepDefaultValues: false, keepDirty: false });
};

export function getEmptyClientDetailsFormValues(): ClientDetailsFormValues {
	return JSON.parse(JSON.stringify(clientDetailsEmptyData));
}

export function getEmptyClientContactFormValues(): ClientContactFormValues {
	const result = {
		...JSON.parse(JSON.stringify(clientContactEmptyData)),
	};
	return result;
}

export function mapUserAccountContactToFormValues(account?: AccountType): UserContactFormValues {
	return {
		firstName: mapStringDefault(account?.firstName),
		middleName: mapStringDefault(account?.middleName),
		lastName: mapStringDefault(account?.lastName),
		preferredContactPhone: mapStringDefaultorEmpty(account?.preferredContactPhone, 'Mobile'),
		mobile: mapStringDefault(account?.mobile),
		alternatePhone: mapStringDefault(account?.alternatePhone),
		streetAddress: account?.streetAddress,
		postalAddress: account?.postalAddress,
		emailAddress: mapStringDefault(account?.email),
		emailVerified: mapBooleanDefault(account?.emailVerified),
		birthDate:
			account?.birthdate && convertDatetoStrYYYYMMDD(new Date(account.birthdate)) !== '1-01-1'
				? convertDatetoStrYYYYMMDD(new Date(account.birthdate))
				: '',
	};
}

export function mapClientToFormValues(client: ClientType): ClientDetailsFormValues {
	return {
		clientName: client.name,
		clientReference: client.reference,
		mailingName: mapStringDefault(client.mailingName),
		legalName: mapStringDefault(client.legalName),
		streetAddress: mapAddress(client.streetAddress),
		postalAddress: mapAddress(client.postalAddress),
		primaryContact: mapStringDefault(client.primaryContact),
	};
}

export function mapClientContactToFormValues(
	clientContact?: ClientContact,
): ClientContactFormValues {
	if (!clientContact) {
		return getEmptyClientContactFormValues();
	}
	return {
		id: clientContact.id,
		firstName: mapStringDefault(clientContact.firstName),
		middleName: mapStringDefault(clientContact.middleName),
		lastName: mapStringDefault(clientContact.lastName),
		preferredContactPhone: mapStringDefaultorEmpty(clientContact?.preferredContactPhone, 'Mobile'),
		mobile: mapStringDefault(clientContact.mobile),
		alternatePhone: mapStringDefault(clientContact.alternatePhone),
		postalAddress: clientContact?.postalAddress,
		streetAddress: clientContact?.streetAddress,
		emailAddress: mapStringDefault(clientContact.email),
		emailVerified: mapBooleanDefault(clientContact?.emailVerified),
		roles: mapClientRoles(clientContact),
	};
}

export function mapClientContactFormValuesToModel(
	formValues: ClientContactFormValues,
	editability: FormEditability<ClientContactFormValues>,
): Partial<ClientContact> {
	const partialClientContact: Partial<ClientContact> = {
		firstName: editability.firstName ? formValues.firstName : undefined,
		lastName: editability.lastName ? formValues.lastName : undefined,
		middleName: editability.middleName ? formValues.middleName : undefined,
		preferredContactPhone: editability.preferredContactPhone
			? formValues.preferredContactPhone
			: undefined,
		mobile: editability.mobile ? formValues.mobile : undefined,
		alternatePhone: editability.alternatePhone ? formValues.alternatePhone : undefined,
		email: editability.emailAddress ? formValues.emailAddress : undefined,
		clientRoles: editability.roles ? undefined : undefined,
	};
	if (editability.postalAddress && formValues.postalAddress) {
		partialClientContact.postalAddress = {
			id: '',
			...formValues.postalAddress,
		};
	}
	if (editability.streetAddress && formValues.streetAddress) {
		partialClientContact.streetAddress = {
			id: '',
			...formValues.streetAddress,
		};
	}
	return removeNullOrUndefinedAttributes(partialClientContact);
}

export function mapAddressToFormValues(address?: Address | AddressWithoutId): AddressFormValues {
	return {
		addressLine1: mapStringDefault(address?.addressLine1),
		addressLine2: mapStringDefault(address?.addressLine2),
		suburb: mapStringDefault(address?.suburb),
		postalCode: mapStringDefault(address?.postalCode),
		state: mapStringDefault(address?.state),
		country: mapStringDefault(address?.country, 'Australia'),
	};
}

export function mapUserContactFormValuesToModel(
	formValues: UserContactFormValues,
	editability: FormEditability<UserContactFormValues>,
): Partial<AccountType> {
	const partialAccount: Partial<AccountType> = {
		firstName: editability.firstName ? formValues.firstName : undefined,
		middleName: editability.middleName ? formValues.middleName : undefined,
		lastName: editability.lastName ? formValues.lastName : undefined,
		preferredContactPhone: editability.preferredContactPhone
			? formValues.preferredContactPhone
			: undefined,
		mobile: editability.mobile ? formValues.mobile : undefined,
		alternatePhone: editability.alternatePhone ? formValues.alternatePhone : undefined,
		email: editability.emailAddress ? formValues.emailAddress : undefined,
		birthdate: editability.birthDate ? formValues.birthDate : undefined,
	};
	if (editability.postalAddress && formValues.postalAddress) {
		partialAccount.postalAddress = { id: '', ...formValues.postalAddress };
	}
	if (editability.streetAddress && formValues.streetAddress) {
		partialAccount.streetAddress = { id: '', ...formValues.streetAddress };
	}
	return removeNullOrUndefinedAttributes(partialAccount);
}

export function mapAddressFormValuesToModel(formValues: AddressFormValues): Omit<Address, 'id'> {
	return {
		addressLine1: formValues.addressLine1,
		addressLine2: formValues.addressLine2,
		suburb: formValues.suburb,
		postalCode: formValues.postalCode,
		state: formValues.state,
		country: formValues.state,
	};
}

export function mapDqpAccountToFormValues(
	userIdentity?: UserIdentityType,
	dqpAccount?: DqpAccountType,
): DqpDetailsFormValues {
	return {
		firstName: mapStringDefault(userIdentity?.firstName),
		lastName: mapStringDefault(userIdentity?.lastName),
		myGovIdEmail: mapStringDefault(userIdentity?.email),
		workEmail: mapStringDefault(dqpAccount?.email),
		workPhone: mapStringDefault(dqpAccount?.mobile),
	};
}

export function mapDqpAccountFormValuesToModel(
	formValues: DqpDetailsFormValues,
	editability: FormEditability<DqpDetailsFormValues>,
) {
	const partialAccount: Partial<DqpAccountType> = {
		firstName: editability.firstName ? formValues.firstName : undefined,
		lastName: editability.lastName ? formValues.lastName : undefined,
		email: editability.workEmail ? formValues.workEmail : undefined,
		mobile: editability.workPhone ? formValues.workPhone : undefined,
	};
	return removeNullOrUndefinedAttributes(partialAccount);
}

export function mapStringDefault(str?: string | null, defaultStr = ''): string {
	return str ? str : defaultStr;
}

export function mapBooleanDefault(val?: boolean, defaultVal = false): boolean {
	return val ? val : defaultVal;
}

export function mapStringDefaultorEmpty(str?: string, defaultStr = ''): string {
	return str ? (str.trim().length == 0 ? defaultStr : str) : defaultStr;
}

export const mapEnumToOptions = <TEnum extends { [id: number]: string }>(
	enumObject: TEnum,
): { value: TEnum[keyof TEnum]; label: string }[] =>
	Object.values(enumObject).map(value => ({
		value: value as TEnum[keyof TEnum],
		label: value,
	}));

export const mapStringArrayToOptions = (list: string[]) =>
	list.map(value => ({
		value,
		label: value,
	}));

export const sortOptionsByLabelAsc = (options: SelectOption[]) =>
	options.sort((a, b) => a.label.localeCompare(b.label));

export function isDirtyFormOnTrim<T extends object>(
	originalDirty: boolean,
	initialValues: T,
	currentValues: T,
) {
	if (!originalDirty) {
		return false;
	} else {
		return Object.keys(initialValues).some(key => {
			const attribute = key as keyof T;
			const isString = typeof currentValues[attribute] == 'string';
			if (!isString) {
				if (typeof currentValues[attribute] == 'object') {
					return initialValues[attribute] != currentValues[attribute];
				} else {
					return originalDirty;
				}
			} else {
				const initialValue: string = initialValues[attribute] as unknown as string;
				const currentValue: string = currentValues[attribute] as unknown as string;
				return initialValue !== currentValue.trim();
			}
		});
	}
}

const serverErrorKeyName = 'serverError';

export const setServerErrorInForm = <
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
	error: ApiError,
) => {
	return form.setError(`root.${serverErrorKeyName}`, {
		type: error.response?.data?.Code,
		message: ApiService.getErrorMessage(error),
	});
};

export const hasServerResponseCodeInForm = (errors: FieldErrors, responseCode: string) =>
	Boolean(
		errors?.root &&
			errors?.root[`${serverErrorKeyName}`] &&
			errors?.root[`${serverErrorKeyName}`].type == responseCode,
	);

export const hasServerErrorInForm = (errors: FieldErrors) =>
	Boolean(errors?.root && errors?.root[`${serverErrorKeyName}`]);

export function isRequiredField<T>(fieldName: keyof T, schema: AnyObject) {
	const field = schema.describe().fields[fieldName];
	const fieldDescription = field as SchemaDescription;
	const isSetAsRequired = fieldDescription.tests.some(test => test.name === 'required');
	const isSetAsOptional = fieldDescription.optional;
	return isSetAsRequired || !isSetAsOptional;
}

/**
 * Prevents the default form submission behavior when the Enter key is pressed within an HTML form.
 * @param {React.KeyboardEvent<HTMLFormElement>} e - The keyboard event triggered by the Enter key press.
 */
export function preventEnterKeySubmission(e: React.KeyboardEvent<HTMLFormElement>) {
	if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
		e.preventDefault();
	}
}

export async function validateFormSchema<T>(schema: AnyObjectSchema, formValues: T) {
	const formErrors: FormErrors<T> = {};
	await schema.validate(formValues, { abortEarly: false }).catch(error => {
		if (error instanceof ValidationError) {
			error.inner.forEach(validationError => {
				const key = validationError.path as keyof T;
				formErrors[key] = validationError.message;
			});
		} else {
			throw error;
		}
	});
	return formErrors;
}

export const printFormValues = (values: FieldValues, formName: string) => {
	console.log(`%c${formName}`, 'color: hotpink'); // eslint-disable-line no-restricted-syntax
	console.log('%cValid form', 'color: green'); // eslint-disable-line no-restricted-syntax
	console.log(values); // eslint-disable-line no-restricted-syntax
};

export const logForm = <
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
) => {
	const values = form.getValues();
	console.log('%cValues:', 'color: cornflowerblue', values); // eslint-disable-line no-restricted-syntax
	const { isDirty, dirtyFields, isSubmitSuccessful, isSubmitted, isValid } = form.formState;
	const hasChanges = hasChangesInForm(form);
	// eslint-disable-next-line no-restricted-syntax
	console.log( '%cForm state:', 'color: cadetblue', 'isDirty', isDirty, '| dirtyFields', dirtyFields, '| isSubmitSuccessful', isSubmitSuccessful, '| isSubmitted', isSubmitted, '| isValid', isValid ); // prettier-ignore
	console.log('%cForm state dirty fields:', 'color: cadetblue', form.formState.dirtyFields); // eslint-disable-line no-restricted-syntax
	console.log('%chas changes in form?', 'color: mediumpurple', hasChanges); // eslint-disable-line no-restricted-syntax
	console.log('%cErrors:', 'color: indianred', form.formState.errors); // eslint-disable-line no-restricted-syntax
	console.log('Number of errors:', Object.keys(form.formState.errors).length); // eslint-disable-line no-restricted-syntax
	alert(
		`Values: ${JSON.stringify(values)}\n\nErrors: ${JSON.stringify(
			form.formState.errors,
		)}\n\nHas changes: ${hasChanges}`,
	);
};

/**
 * Checks if the form has any changes in the form state.
 * If the main flag isDirty from the form state is false, it looks for any dirty fields
 * and any nested dirty fields.
 * The main flag isDirty might not to be updated in some special cases.
 * @param form The form to check for changes.
 * @returns {boolean} true if the form has any changes, false otherwise.
 */
export const hasChangesInForm = <
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
) => {
	const { isDirty, dirtyFields } = form.formState;
	if (isDirty) {
		return true;
	}
	return checkIsDirtyAnyField(dirtyFields, []);
};

export const hasChangesInFormPartially = <
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
	fieldsToExclude: string[],
) => {
	const { dirtyFields } = form.formState;
	const isDirtyAnyField = checkIsDirtyAnyField(dirtyFields, fieldsToExclude);
	return isDirtyAnyField;
};

const checkIsDirtyAnyField = (dirtyFields: DirtyFields, fieldsToExclude: string[] = []) => {
	const filteredDirtyFields = { ...dirtyFields };
	for (const fieldName of fieldsToExclude) {
		if (Object.keys(filteredDirtyFields).includes(fieldName)) {
			delete filteredDirtyFields[fieldName];
		}
	}
	return Object.values(filteredDirtyFields).some(dirtyness => {
		if (typeof dirtyness === 'boolean') {
			return dirtyness;
		} else if (Array.isArray(dirtyness)) {
			const isDirtyAnyNested = dirtyness.some(nestedDirtyFields => checkIsDirtyAnyField(nestedDirtyFields)); // prettier-ignore
			return isDirtyAnyNested;
		}
	});
};

export function generateFieldId(fieldName: string) {
	return `${random(9999)}-${fieldName}`;
}

function mapClientRoles(clientDetails: ClientContact | undefined): string[] {
	if (!clientDetails) {
		return [''];
	}
	return clientDetails.clientRoles.map(clientRole =>
		[clientRole.name, clientRole.role].filter(str => str && str.length > 0).join(' as '),
	);
}

function removeNullOrUndefinedAttributes<T>(obj: T): T {
	for (const key in obj) {
		if (obj[key] === null || obj[key] === undefined) {
			delete obj[key];
		}
	}
	return obj;
}
/**
 * Set restored values into parent form and subforms
 * @param {string} fileData
 */
export const importJsonToForms = (
	data: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	parentForm: UseFormReturn<any>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	subformsList: readonly UseFormReturn<any>[],
) => {
	const jsonData = JSON.parse(data);
	Object.entries(jsonData).forEach(([key, value]) => {
		const formName = key as ModuleASubformName;
		const formValues = value as Partial<typeof parentForm.getValues>;
		for (const subform of subformsList) {
			const doesMatchSubformName =
				subform.control._options.context && subform.control._options.context.formName === formName;
			if (doesMatchSubformName) {
				subform.reset(formValues);
				subform.trigger(); // Revalidate subform
				parentForm.setValue(formName, formValues);
				break;
			}
		}
	});
	parentForm.trigger(); // Revalidate parent form
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const exportFormsToJson = (parentForm: UseFormReturn<any>, moduleAFormVersion: string) => {
	type ModuleAFormExport = Partial<typeof parentForm.getValues> & Record<'metadata', unknown>;
	const formsValues: ModuleAFormExport = Object(parentForm.getValues());

	// Add metadata: version and export date
	formsValues.metadata = {
		waterIQVersion: [
			process.env.WIQ_BUILD_NUMBER,
			process.env.WIQ_RELEASE_NUMBER,
			process.env.WIQ_RELEASE_DATE,
		].join(' - '),
		formVersion: moduleAFormVersion,
		exportDate: getCurrentDateTime(),
	};

	return JSON.stringify(formsValues);
};

/**
 * Determines if a field is required based on its name and the corresponding schema.
 * @template T - The type of the object containing the field.
 * @param {keyof T} fieldName - The name of the field.
 * @param {Yup.AnyObject} schema - The Yup schema defining the field.
 * @param {FieldValues | undefined} [values] - The current values of the form. For performance, use hook watch from React Hook Form
 * @returns {boolean} - True if the field is required, false otherwise.
 */
export const isRequired = <T>(
	fieldName: keyof T,
	schema: Yup.AnyObject,
	values: FieldValues,
	formContext?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
): boolean => {
	// Check if the field is an array field
	let isArraySchema = false;
	let parentFieldName = '';
	let childFieldName = '';
	if (fieldName.toString().includes('.')) {
		parentFieldName = fieldName.toString().split('.')[0];
		childFieldName = fieldName.toString().split('.').reverse()[0];
		const parentField = schema.describe({ value: values, context: formContext }).fields[
			parentFieldName
		];
		const parentFieldDescription = parentField as Yup.SchemaDescription | undefined;
		isArraySchema = parentFieldDescription?.type === 'array';
	}

	// Get the field description from the schema.
	let field;
	if (isArraySchema) {
		field = schema.describe({ value: values, context: formContext }).fields[parentFieldName]
			?.innerType?.fields[childFieldName];
	} else {
		field = schema.describe({ value: values, context: formContext }).fields[fieldName];
	}
	const fieldDescription = field as Yup.SchemaDescription | undefined;
	if (!fieldDescription) {
		// If field description is undefined, the field is not defined in the schema so it is not required
		return false;
	}

	// Determine if the field is required based on its schema definition
	const isSetAsRequired = fieldDescription.tests.some(test => test.name === 'required');
	const isSetAsOptional = fieldDescription.optional;
	const isRequired = isSetAsRequired || !isSetAsOptional;
	return isRequired;
};

/**
 * Update specified fields for a form using the provided values
 * @param {FormAny} form - The form to update.
 * @param {Array<keyof T>} fieldsToUpdate - The list of fields to update.
 * @param {T | undefined} formValues - The values to update the form with.
 */
export const updateFormValues = <
	T extends object,
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
	fieldsToUpdate: Array<keyof T>,
	formValues: T | undefined,
) => {
	if (!formValues || !fieldsToUpdate.length) return;
	fieldsToUpdate.forEach(field => {
		form.setValue(field as any, formValues[field] as any); // eslint-disable-line @typescript-eslint/no-explicit-any
	});
};

export const setOnEditModeByInvalidNestedForm = (
	nestedFormsValues: Array<ActionableFormValues>,
	setEditingIndices: Dispatch<SetStateAction<Set<number>>>,
	schema: AnyObjectSchema,
	formContext?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
	for (let i = nestedFormsValues.length - 1; i >= 0; i--) {
		const nestedFormValues = nestedFormsValues[i];
		const isValid = schema.isValidSync(nestedFormValues, formContext);
		if (!isValid) {
			console.log(`invalid nested element index:${i} id:${(nestedFormValues as ActionableFormValues).id}`); // eslint-disable-line no-restricted-syntax
			setEditingIndices(prev => new Set([...prev, i]));
		}
	}
};

/**
 * Creates a function to hide an alert based on an event type.
 * @param setShowAlert - A state setter function to control the visibility of the alert.
 * @returns A callback that hides the alert if the event type is 'change'.
 */
export function hideAlertFactory(setShowAlert: Dispatch<SetStateAction<boolean>>) {
	return (eventType?: unknown) => {
		const canClearAlert = eventType === 'change';
		if (canClearAlert) {
			setShowAlert(false);
		}
	};
}

/**
 * Custom hook that automatically hides an alert when a form field changes.
 *
 * @param form - The form object that provides the watch method to monitor field changes.
 * @param setState - A state setter function to control the visibility of the alert.
 *
 * This hook uses the `useEffect` hook to subscribe to form field changes via the `watch` method.
 * When a change event is detected, it triggers the `clearAlert` function to hide the alert.
 * The subscription is cleaned up when the component unmounts or dependencies change.
 */
export function useHideAlertOnChange<
	TFieldValues extends FieldValues,
	TContext = any, // eslint-disable-line @typescript-eslint/no-explicit-any
	TTransformedValues extends FieldValues | undefined = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
	setState: Dispatch<SetStateAction<boolean>>,
) {
	useEffect(() => {
		const clearAlert = hideAlertFactory(setState);
		const subscription = form.watch((value: unknown, { type }: { type?: EventType }) =>
			clearAlert(type),
		);
		return () => subscription.unsubscribe();
	}, [form, setState]);
}
